import React from 'react';
import { useAppStore } from '../stores/appStore';
import { useNextCard, useSubmitAnswer } from '../hooks/useCards';
import { ErrorMessage } from './ErrorMessage';

export const StudyView: React.FC = () => {
  const { showDefinition, setShowDefinition } = useAppStore();
  const { data: response, isLoading, error, refetch } = useNextCard();
  const submitAnswerMutation = useSubmitAnswer();

  const handleShowDefinition = () => {
    setShowDefinition(true);
  };

  const handleAnswer = async (correct: boolean) => {
    if (!response?.card) return;

    try {
      await submitAnswerMutation.mutateAsync({
        cardId: response.card.id,
        correct,
      });
      // Reset definition visibility for next card
      setShowDefinition(false);
    } catch (error) {
      console.error('Failed to submit answer:', error);
    }
  };

  // Show error state with retry option
  if (error) {
    return (
      <div>
        <ErrorMessage error={(error as any)?.message || 'Failed to load card'} className="mb-6" />
        <div className="text-center">
          <button
            onClick={() => refetch()}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-600 mt-4">Loading next card...</p>
      </div>
    );
  }

  // Status messages when no card available
  if (!response?.card) {
    return (
      <div className="text-center py-12">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 max-w-md mx-auto">
          <div className="text-4xl mb-4">🎉</div>
          <p className="text-lg text-gray-700 mb-4">
            {response?.message || 'No cards available'}
          </p>
          <button
            onClick={() => refetch()}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Check Again
          </button>
        </div>
      </div>
    );
  }

  const { card } = response;

  return (
    <div className="max-w-2xl mx-auto">
      {/* Card Display */}
      <div className="bg-white rounded-lg shadow-lg p-8 mb-6 min-h-[300px] flex flex-col justify-center">
        {/* Word */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">{card.word}</h2>
          
          {/* Card Info */}
          <div className="text-sm text-gray-500">
            Bin {card.current_bin} • {card.incorrect_count} errors
          </div>
        </div>

        {/* Definition (shown after clicking "Show Definition") */}
        {showDefinition && (
          <div className="text-center border-t pt-6">
            <p className="text-xl text-gray-700 leading-relaxed">{card.definition}</p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="text-center">
        {!showDefinition && (
          <button
            onClick={handleShowDefinition}
            className="bg-blue-600 text-white px-8 py-3 rounded-md text-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Show Definition
          </button>
        )}

        {showDefinition && (
          <div className="space-x-4">
            <button
              onClick={() => handleAnswer(false)}
              disabled={submitAnswerMutation.isPending}
              className="bg-red-600 text-white px-8 py-3 rounded-md text-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitAnswerMutation.isPending ? 'Submitting...' : 'I Did Not Get It'}
            </button>
            <button
              onClick={() => handleAnswer(true)}
              disabled={submitAnswerMutation.isPending}
              className="bg-green-600 text-white px-8 py-3 rounded-md text-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitAnswerMutation.isPending ? 'Submitting...' : 'I Got It'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};